import React, { useCallback, useMemo, useRef } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getWfhModuleCapabilities } from '../../config/wfhCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useWfhRequests } from '../../hooks/useWfhRequests';
import type { WfhRequestListItem } from '../../types/wfh';
import {
  formatAttendanceWeekday,
  formatCalendarDate,
} from '../../utils/dateTime';
import { createDefaultWfhFilters } from '../../utils/wfhFilters';
import { getWfhStatusCategory } from '../../utils/wfhStatus';
import { formatWfhDurationLabel } from '../../utils/wfhSession';

type Props = NativeStackScreenProps<RootStackParamList, 'WfhRequests'>;

function statusTextColor(statusCode: string | null, palette: ColorPalette) {
  const category = getWfhStatusCategory(statusCode);
  if (category === 'success') return palette.success;
  if (category === 'danger') return palette.error;
  if (category === 'warning') return palette.warning;
  if (category === 'info') return palette.info;
  return palette.textSecondary;
}

function WfhRequestRow({
  item,
  onPress,
  palette,
}: {
  item: WfhRequestListItem;
  onPress: () => void;
  palette: ColorPalette;
}) {
  const dateLabel = formatCalendarDate(item.fromDate);
  const weekday = formatAttendanceWeekday(item.fromDate) || '—';
  const duration = formatWfhDurationLabel(item);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${dateLabel}, ${duration ? `${duration}, ` : ''}status ${item.statusLabel}`}
      onPress={onPress}
      style={[styles.row, { borderBottomColor: palette.border }]}
    >
      <View style={styles.rowLeft}>
        <Text style={[styles.title, { color: palette.textPrimary }]}>
          {dateLabel}
        </Text>
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          {duration ? `${weekday} · ${duration}` : weekday}
        </Text>
      </View>
      <View style={styles.rowRight}>
        <Text
          style={[
            styles.status,
            { color: statusTextColor(item.statusCode, palette) },
          ]}
        >
          {item.statusLabel}
        </Text>
        <Text style={[styles.meta, { color: palette.textPrimary }]}>
          Work From Home
        </Text>
      </View>
    </Pressable>
  );
}

export function WfhRequestListScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getWfhModuleCapabilities(auth.permissions);
  const history = useWfhRequests();
  const {
    applyFilters,
    loadRequests,
    refreshRequests,
    resetFilters,
    retry,
    filters,
    items,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    loadMore,
  } = history;
  const defaults = useMemo(() => createDefaultWfhFilters(), []);
  const hasFocused = useRef(false);

  const themed = useMemo(
    () => ({
      range: {
        ...typography.body,
        color: palette.textSecondary,
        marginBottom: spacing.md,
      },
      warning: {
        ...typography.caption,
        color: palette.warning,
        marginBottom: spacing.md,
      },
    }),
    [palette],
  );

  useFocusEffect(
    useCallback(() => {
      const applied = route.params?.appliedFilters;
      if (applied) {
        applyFilters(applied);
        navigation.setParams({ appliedFilters: undefined });
        hasFocused.current = true;
        return;
      }
      if (!hasFocused.current) {
        hasFocused.current = true;
        loadRequests();
        return;
      }
      refreshRequests();
    }, [
      applyFilters,
      loadRequests,
      navigation,
      refreshRequests,
      route.params?.appliedFilters,
    ]),
  );

  const filtersCustomized =
    filters.fromDate !== defaults.fromDate ||
    filters.toDate !== defaults.toDate;

  const footer = useMemo(() => {
    if (!isLoadingMore) return null;
    return (
      <View
        style={styles.footer}
        accessibilityLabel="Loading more WFH requests"
      >
        <ActivityIndicator color={palette.primary} />
      </View>
    );
  }, [isLoadingMore, palette.primary]);

  return (
    <PermissionGuard
      permission={PERMISSIONS.WFH_APPLY}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader
          bleed={false}
          title="WFH Requests"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
          <Text style={themed.range}>
            {filters.fromDate} to {filters.toDate}
          </Text>
          <View style={styles.actions}>
            <AppButton
              title="Filters"
              variant="secondary"
              onPress={() =>
                navigation.navigate(routes.wfhFilter, {
                  currentFilters: filters,
                })
              }
            />
          </View>
          {!capabilities.canApply ? (
            <Text style={themed.warning}>
              Apply WFH is not currently available.
            </Text>
          ) : null}

          {isLoading && items.length === 0 ? (
            <View style={styles.center}>
              <ActivityIndicator
                color={palette.primary}
                accessibilityLabel="Loading WFH requests"
              />
            </View>
          ) : error && items.length === 0 ? (
            <ErrorState message={error} onRetry={retry} />
          ) : (
            <FlatList
              data={items}
              keyExtractor={item => String(item.wfhRequestId)}
              refreshControl={
                <RefreshControl
                  refreshing={isRefreshing}
                  onRefresh={refreshRequests}
                />
              }
              onEndReached={loadMore}
              onEndReachedThreshold={0.4}
              ListFooterComponent={footer}
              ListEmptyComponent={
                <EmptyState
                  title="No WFH requests"
                  message="No WFH requests were found for this period."
                  actionTitle={filtersCustomized ? 'Reset filters' : 'Retry'}
                  onAction={filtersCustomized ? resetFilters : retry}
                />
              }
              contentContainerStyle={styles.listContent}
              renderItem={({ item }) => (
                <WfhRequestRow
                  item={item}
                  palette={palette}
                  onPress={() =>
                    navigation.navigate(routes.wfhDetails, {
                      wfhRequestId: item.wfhRequestId,
                    })
                  }
                />
              )}
            />
          )}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0, paddingHorizontal: 0, paddingVertical: 0 },
  body: {
    flex: 1,
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.lg,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl },
  row: {
    paddingVertical: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  rowLeft: { flex: 1, gap: spacing.xs },
  rowRight: { alignItems: 'flex-end', gap: spacing.xs },
  title: { ...typography.body, fontWeight: '700' },
  meta: { ...typography.caption },
  status: { ...typography.body, fontWeight: '700' },
  footer: { paddingVertical: spacing.lg, alignItems: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
